import { z } from "zod";
import { keys } from "@/lib/i18n/portal";

const toNumber = (value: unknown) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }
  return value;
};

export const buildProductDetailsSchema = (t: (key: string) => string) =>
  z
    .object({
      nameAr: z.string().trim().min(1, t(keys.errors.required)),
      nameEn: z.preprocess(
        (value) =>
          typeof value === "string" && value.trim() === "" ? undefined : value,
        z.string().trim().min(2, t(keys.errors.required)).optional()
      ),
      shortDescriptionAr: z.preprocess(
        (value) =>
          typeof value === "string" && value.trim() === "" ? undefined : value,
        z.string().trim().min(1, t(keys.errors.required)).max(280).optional()
      ),
      shortDescriptionEn: z.preprocess(
        (value) =>
          typeof value === "string" && value.trim() === "" ? undefined : value,
        z.string().trim().max(280).optional()
      ),
      categoryIds: z.array(z.string()).optional(),
      price: z.preprocess(
        toNumber,
        z
          .number({
            required_error: t(keys.errors.required),
            invalid_type_error: t(keys.errors.required),
          })
          .min(0, t(keys.errors.mustBeNonNegative))
      ),
      discountedPrice: z.preprocess(
        toNumber,
        z.number().min(0, t(keys.errors.mustBeNonNegative)).optional()
      ),
      // Seller goods cost for the (default) variant — for profit/margin.
      // Optional: blank clears it. Not compared to price (cost may exceed it).
      cost: z.preprocess(
        toNumber,
        z.number().min(0, t(keys.errors.mustBeNonNegative)).optional()
      ),
      stockCount: z.preprocess(
        toNumber,
        z
          .number({
            required_error: t(keys.errors.required),
            invalid_type_error: t(keys.errors.required),
          })
          .min(0, t(keys.errors.mustBeNonNegative))
      ),
      forceOutOfStock: z.boolean(),
      isActive: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (
        typeof values.discountedPrice === "number" &&
        values.discountedPrice >= values.price
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["discountedPrice"],
          message: t(keys.errors.discountedPriceMustBeLessThanPrice),
        });
      }
      // Cost may equal the price (break-even) but never exceed it.
      if (typeof values.cost === "number" && values.cost > values.price) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["cost"],
          message: t(keys.errors.costMustNotExceedPrice),
        });
      }
    });

export type ProductDetailsFormValues = z.infer<
  ReturnType<typeof buildProductDetailsSchema>
>;
